// ACA / Health Workspace — 6 components from the architecture diagram.
//   #1 OEPCommandCenter      — countdown + KPIs
//   #2 SEPEligibilityQualifier — 5 ACA-specific life event types
//   #3 APTCCalculator        — household + income → net premium + CSR flag
//   #4 FFMAccountPanel       — NPI, AOR, marketplace IDs
//   #5 EffectuationTracker   — Sold → Pending → Effectuated → Lapsed
//   #6 DocumentCollectionPanel — request → upload → carrier submission

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Calendar, Calculator, FileText, ShieldCheck, AlertTriangle, Plus, Send, CheckCircle, X, Upload, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { healthSherpaQuoteUrl } from '../lib/healthSherpaLink';
import { upload } from '@vercel/blob/client';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const SEP_KINDS = [
  { id: 'loss_of_coverage',   label: 'Loss of qualifying coverage' },
  { id: 'household_change',   label: 'Household change (marriage / birth / etc.)' },
  { id: 'residence_change',   label: 'Residence change' },
  { id: 'eligibility_change', label: 'Eligibility change (income / immigration)' },
  { id: 'other_exceptional',  label: 'Other / exceptional circumstance' },
];

const DOC_KINDS = [
  { id: 'income_verification',    label: 'Income verification' },
  { id: 'citizenship',            label: 'Citizenship / immigration' },
  { id: 'ssn',                    label: 'SSN' },
  { id: 'address',                label: 'Address' },
  { id: 'sep_qualifying_event',   label: 'SEP qualifying event proof' },
  { id: 'health_screening',       label: 'Health screening' },
  { id: 'replacement_form',       label: 'Replacement form' },
  { id: 'beneficiary_change',     label: 'Beneficiary change' },
  { id: 'soa',                    label: 'SOA' },
  { id: 'other',                  label: 'Other' },
];

const EFF_STAGES = [
  { id: 'sold',         label: 'Sold',        tone: 'bg-slate-50 border-slate-200' },
  { id: 'pending',      label: 'Pending',     tone: 'bg-amber-50 border-amber-200' },
  { id: 'effectuated',  label: 'Effectuated', tone: 'bg-emerald-50 border-emerald-200' },
  { id: 'lapsed',       label: 'Lapsed',      tone: 'bg-rose-50 border-rose-200' },
];

export default function AcaWorkspaceView({ currentUser, showToast = () => {}, openClientProfile = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await apiFetch('/api/aca/dashboard')); }
    catch (err) { setError(String(err.message || err)); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading ACA workspace…</div>;
  if (error)   return <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800">{error}</div>;
  if (!data)   return null;

  return (
    <div className="space-y-5">
      <OEPCommandCenter data={data}/>
      <EffectuationTracker effectuation={data.effectuation} openClientProfile={openClientProfile}/>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <APTCCalculator onReload={load} showToast={showToast} recent={data.snapshots} openClientProfile={openClientProfile}/>
        <SEPEligibilityQualifier seps={data.seps} onReload={load} showToast={showToast} openClientProfile={openClientProfile}/>
      </div>
      <DocumentCollectionPanel docs={data.docs} onReload={load} showToast={showToast} openClientProfile={openClientProfile}/>
      <FFMAccountPanel credentials={data.credentials} onReload={load} showToast={showToast}/>
    </div>
  );
}

// ── #1 OEP Command Center ───────────────────────────────────────────
function OEPCommandCenter({ data }) {
  const { oep, kpis } = data;
  const phaseLabel = oep.phase === 'pre' ? 'OEP starts' : oep.phase === 'active' ? 'OEP active — closes' : 'Next OEP starts';
  const phaseTone  = oep.phase === 'active' ? 'bg-emerald-50 border-emerald-300 text-emerald-900' :
                     oep.phase === 'pre'    ? 'bg-amber-50 border-amber-300 text-amber-900' :
                                              'bg-slate-50 border-slate-300 text-slate-700';
  return (
    <div className="bg-white border-2 border-emerald-900 rounded-xl overflow-hidden">
      <div className="bg-emerald-900 text-white px-5 py-3">
        <div className="text-emerald-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">ACA · OEP Command Center</div>
        <div className="text-white text-lg font-bold mt-0.5">Nov 1 – Jan 15 Open Enrollment</div>
        {/* Shipped-features indicators — same pattern as Medicare. Per
            audit response §18 Phase 9 "OEP countdown alerts per client"
            was flagged as missing; it's shipped via the universal
            workflow engine 'oep_pre_renewal' step at day 290. */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 rounded px-1.5 py-0.5" title="Auto-trigger OEP re-shop nudge at day 290 from policy issue — workflow engine step 'oep_pre_renewal'">
            ✓ Per-client OEP countdown
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 rounded px-1.5 py-0.5" title="ANCHOR closes Marketplace issues (AOR, income docs, payment failures) with bounded cadence + hard stops">
            ✓ ANCHOR retention agent
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 rounded px-1.5 py-0.5" title="ACA consent-to-assist line auto-appended to every Marketplace message where required by CMS">
            ✓ Consent-to-assist
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 p-3">
        <div className={`border-2 rounded-md p-3 ${phaseTone}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-75">{phaseLabel}</div>
          <div className="text-2xl font-black tabular-nums mt-0.5">{oep.days} <span className="text-xs">d</span></div>
          <div className="text-[10px] opacity-75 mt-0.5">{oep.phase === 'active' ? `until ${oep.ends_on}` : `on ${oep.starts_on}`}</div>
        </div>
        <KpiBox label="Sold"        value={kpis.sold}        tone="slate"/>
        <KpiBox label="Pending"     value={kpis.pending}     tone="amber"/>
        <KpiBox label="Effectuated" value={kpis.effectuated} tone="emerald"/>
        <KpiBox label="Open SEPs"   value={kpis.sep_open}    sub={`${kpis.sep_expiring_soon} ≤14d`} tone="sky"/>
        <KpiBox label="Docs outstanding" value={kpis.docs_outstanding} sub={`${kpis.docs_overdue} overdue`} tone="rose"/>
      </div>
    </div>
  );
}

function KpiBox({ label, value, sub, tone }) {
  const toneClass = tone === 'rose' ? 'bg-rose-50 border-rose-200 text-rose-900' :
                    tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                    tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' :
                    tone === 'sky'   ? 'bg-sky-50 border-sky-200 text-sky-900' :
                                       'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`border-2 rounded-md p-3 ${toneClass}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-75">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-0.5">{(value ?? 0).toLocaleString()}</div>
      {sub && <div className="text-[10px] opacity-75 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── #5 Effectuation Tracker (4-stage board) ─────────────────────────
function EffectuationTracker({ effectuation, openClientProfile = () => {} }) {
  const list = effectuation || [];
  const byStage = EFF_STAGES.reduce((m, s) => { m[s.id] = []; return m; }, {});
  for (const p of list) {
    (byStage[p.effectuation_stage] || byStage.sold).push(p);
  }
  if (list.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Effectuation Pipeline</div>
        <div className="text-sm text-slate-500 italic mt-2">No ACA Marketplace policies on file yet.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700">
        Effectuation Pipeline · Sold → Pending → Effectuated → Lapsed ({list.length} policies)
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-3">
        {EFF_STAGES.map(s => (
          <div key={s.id} className={`border-2 ${s.tone} rounded-lg p-2 min-h-[160px]`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 px-1 pb-2 flex items-center justify-between">
              {s.label}
              <span className="tabular-nums text-slate-500">{byStage[s.id].length}</span>
            </div>
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {byStage[s.id].slice(0, 30).map(p => (
                <div key={p.policy_id} className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs">
                  {p.client_name
                    ? <button type="button" onClick={() => openClientProfile(p.client_id || p.client_name)} className="font-bold text-slate-900 truncate hover:underline text-left w-full">{p.client_name}</button>
                    : <div className="font-bold text-slate-900 truncate">—</div>}
                  <div className="text-[10px] text-slate-500">{p.carrier} · {p.plan_name}</div>
                  {p.monthly_premium && <div className="text-[10px] text-slate-700 tabular-nums">${Number(p.monthly_premium).toFixed(0)}/mo</div>}
                  {p.effective_date && <div className="text-[10px] text-slate-500">Eff {p.effective_date}</div>}
                </div>
              ))}
              {byStage[s.id].length === 0 && <div className="text-[10px] text-slate-400 italic text-center py-2">empty</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── #3 APTC Calculator ──────────────────────────────────────────────
function APTCCalculator({ onReload, showToast, recent = [], openClientProfile = () => {} }) {
  const [form, setForm] = React.useState({
    client_name:         '',
    household_size:      1,
    annual_income:       '',
    plan_year:           new Date().getFullYear(),
    benchmark_plan_cost: '',
    state_code:          '',
    zip:                 '',
    age:                 '',
  });
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // Subsidy-change alert — when the operator types a new income on a
  // client we already have a snapshot for, surface a delta warning.
  const [alertMsg, setAlertMsg] = React.useState(null);
  React.useEffect(() => {
    if (!form.client_name || !form.annual_income) { setAlertMsg(null); return; }
    const last = (recent || []).find(s => s.client_name && s.client_name.trim().toLowerCase() === form.client_name.trim().toLowerCase());
    if (!last) { setAlertMsg(null); return; }
    const newInc = Number(form.annual_income);
    if (!Number.isFinite(newInc) || newInc === Number(last.snapshotted_income)) { setAlertMsg(null); return; }
    const incomeDelta = newInc - Number(last.snapshotted_income || 0);
    setAlertMsg({
      kind: Math.abs(incomeDelta) >= 5000 ? 'big' : 'small',
      msg:  `Heads-up: this client's last snapshot (${new Date(last.snapshotted_at).toLocaleDateString()}) was on $${Number(last.snapshotted_income).toLocaleString()} income with $${last.snapshotted_aptc}/mo APTC. New income is $${newInc.toLocaleString()} (Δ ${incomeDelta >= 0 ? '+' : ''}$${incomeDelta.toLocaleString()}). Re-run to confirm APTC + update FFM if it shifts.`,
    });
  }, [form.client_name, form.annual_income, recent]);

  const calc = async () => {
    if (!form.annual_income || !form.benchmark_plan_cost) {
      showToast('Income + benchmark premium required.');
      return;
    }
    setBusy(true);
    try {
      const j = await apiFetch('/api/aca/actions', {
        method: 'POST',
        body: JSON.stringify({ action: 'subsidy_calc', ...form, annual_income: Number(form.annual_income), benchmark_plan_cost: Number(form.benchmark_plan_cost), household_size: Number(form.household_size) }),
      });
      setResult(j.calc);
      showToast('Calculator ran + snapshot saved.');
      onReload();
    } catch (err) { showToast(`Calc failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
        <Calculator size={11}/> APTC / Subsidy Calculator
      </div>
      <div className="px-4 py-3 space-y-2">
        <input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Client name (optional, for the snapshot)" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-xs text-slate-600">Household size
            <input type="number" min="1" value={form.household_size} onChange={e => setForm({ ...form, household_size: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">Annual income ($)
            <input type="number" value={form.annual_income} onChange={e => setForm({ ...form, annual_income: e.target.value })} placeholder="50000" className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">Plan year
            <input type="number" value={form.plan_year} onChange={e => setForm({ ...form, plan_year: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">2nd-lowest Silver $/mo
            <input type="number" value={form.benchmark_plan_cost} onChange={e => setForm({ ...form, benchmark_plan_cost: e.target.value })} placeholder="475" className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">ZIP
            <input value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value.replace(/[^0-9]/g, '').slice(0, 5) })} placeholder="78704" className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">Age (head of household)
            <input type="number" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
        </div>
        {alertMsg && (
          <div className={`border-2 rounded-md px-3 py-2 text-xs ${alertMsg.kind === 'big' ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-sky-50 border-sky-200 text-sky-800'}`}>
            <strong>{alertMsg.kind === 'big' ? '⚠ Subsidy may shift significantly' : 'ℹ Income changed since last snapshot'}</strong>
            <div className="mt-0.5">{alertMsg.msg}</div>
          </div>
        )}
        <button onClick={calc} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-1.5 rounded-md">
          {busy ? 'Calculating…' : 'Calculate APTC'}
        </button>
        {result && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-md p-3 mt-2 space-y-1 text-sm">
            <div><strong>FPL:</strong> ${result.fpl_dollars?.toLocaleString()} ({result.fpl_percent}%)</div>
            <div><strong>Expected monthly contribution:</strong> ${result.expected_contribution_monthly}/mo ({result.expected_contribution_pct != null ? (result.expected_contribution_pct * 100).toFixed(2) + '%' : 'cliff'})</div>
            <div className="text-lg font-bold text-emerald-900"><strong>Estimated APTC:</strong> ${result.aptc_monthly}/mo</div>
            <div><strong>Net premium (benchmark – APTC):</strong> ${Math.max(0, (Number(form.benchmark_plan_cost) || 0) - (result.aptc_monthly || 0)).toFixed(2)}/mo</div>
            {result.csr_silver_eligible && (
              <div className="bg-violet-50 border border-violet-300 rounded px-2 py-1 text-violet-900 text-xs font-bold">✓ Eligible for CSR Silver {result.csr_variant} variant</div>
            )}
            {result.cliff_applied && (
              <div className="bg-amber-50 border border-amber-300 rounded px-2 py-1 text-amber-900 text-xs">{result.note}</div>
            )}
            {/* HealthSherpa deep-link — opens their quoter prefilled. */}
            <button
              onClick={() => {
                const url = healthSherpaQuoteUrl({
                  zip:            form.zip,
                  household_size: form.household_size,
                  age:            form.age,
                  income:         form.annual_income,
                  plan_year:      form.plan_year,
                });
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              className="mt-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
            >
              <ExternalLink size={11}/> Open in HealthSherpa quoter
            </button>
            <div className="text-[10px] text-slate-500 italic mt-1">Estimate only — verify on HealthCare.gov before enrollment.</div>
          </div>
        )}
        {recent.length > 0 && (
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer font-bold text-slate-700">Recent snapshots ({recent.length})</summary>
            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
              {recent.map(s => (
                <div key={s.id} className="border-b border-slate-100 pb-1 text-xs">
                  {s.client_name
                    ? <button type="button" onClick={() => openClientProfile(s.client_id || s.client_name)} className="font-bold hover:underline">{s.client_name}</button>
                    : <span className="font-bold">unnamed</span>}
                  {' '}· FPL {s.fpl_percent}% · APTC ${s.aptc_monthly}/mo {s.csr_silver_eligible && <span className="text-violet-700">CSR {s.csr_variant}</span>} · {new Date(s.created_at).toLocaleDateString()}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// ── #2 SEP Eligibility Qualifier ────────────────────────────────────
function SEPEligibilityQualifier({ seps, onReload, showToast, openClientProfile = () => {} }) {
  const [show, setShow] = React.useState(false);
  const [f, setF] = React.useState({ client_name: '', client_phone: '', event_kind: 'loss_of_coverage', event_date: '', sep_window_days: 60, notes: '' });
  const log = async () => {
    if (!f.client_name.trim() || !f.event_date) { showToast('Client name + event date required.'); return; }
    try {
      await apiFetch('/api/aca/actions', { method: 'POST', body: JSON.stringify({ action: 'sep_log', ...f, sep_window_days: Number(f.sep_window_days) || 60 }) });
      showToast('SEP logged + task created.');
      setF({ client_name: '', client_phone: '', event_kind: 'loss_of_coverage', event_date: '', sep_window_days: 60, notes: '' });
      setShow(false);
      onReload();
    } catch (err) { showToast(`Log failed: ${err.message}`); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><Calendar size={11}/> SEP Eligibility ({(seps || []).length} open)</div>
        <button onClick={() => setShow(!show)} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={11}/> Log SEP</button>
      </div>
      {show && (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={f.client_name} onChange={e => setF({ ...f, client_name: e.target.value })} placeholder="Client name" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <input value={f.client_phone} onChange={e => setF({ ...f, client_phone: e.target.value })} placeholder="Phone (optional)" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </div>
          <select value={f.event_kind} onChange={e => setF({ ...f, event_kind: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
            {SEP_KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="date" value={f.event_date} onChange={e => setF({ ...f, event_date: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <input type="number" value={f.sep_window_days} onChange={e => setF({ ...f, sep_window_days: e.target.value })} placeholder="Window days" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </div>
          <input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="Notes / documentation needed" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          <div className="flex gap-2">
            <button onClick={log} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-1.5 rounded-md">Log + auto-task</button>
            <button onClick={() => setShow(false)} className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}
      {(seps || []).length === 0 ? (
        <div className="px-4 py-4 text-sm text-slate-500 italic">No open SEP events.</div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {(seps || []).slice(0, 30).map(s => {
            const tone = s.days_remaining <= 7 ? 'bg-rose-50 text-rose-700 border-rose-200' : s.days_remaining <= 21 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-700 border-slate-200';
            return (
              <div key={s.id} className="px-4 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {s.client_name
                    ? <button type="button" onClick={() => openClientProfile(s.client_id || s.client_name)} className="font-bold text-slate-900 text-sm hover:underline">{s.client_name}</button>
                    : <span className="font-bold text-slate-900 text-sm">—</span>}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{String(s.event_kind).replace(/_/g, ' ')}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone}`}>{s.days_remaining}d left</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Event {s.event_date} · expires {s.expires_on}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── #6 Document Collection ──────────────────────────────────────────
function DocumentCollectionPanel({ docs, onReload, showToast, openClientProfile = () => {} }) {
  const [show, setShow] = React.useState(false);
  const [f, setF] = React.useState({ client_name: '', document_kind: 'income_verification', description: '', due_date: '', requested_via: 'sms' });

  const request = async () => {
    if (!f.client_name.trim()) { showToast('Client name required.'); return; }
    try {
      await apiFetch('/api/aca/actions', { method: 'POST', body: JSON.stringify({ action: 'doc_request', ...f, due_date: f.due_date || null }) });
      showToast('Document requested.');
      setF({ client_name: '', document_kind: 'income_verification', description: '', due_date: '', requested_via: 'sms' });
      setShow(false);
      onReload();
    } catch (err) { showToast(`Request failed: ${err.message}`); }
  };

  const setStatus = async (id, status, extra = {}) => {
    try {
      await apiFetch('/api/aca/actions', { method: 'POST', body: JSON.stringify({ action: 'doc_status', id, status, ...extra }) });
      onReload();
    } catch (err) { showToast(`Update failed: ${err.message}`); }
  };

  const statusTone = (s) =>
    s === 'accepted'             ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    s === 'rejected'             ? 'bg-rose-50 text-rose-700 border-rose-200' :
    s === 'submitted_to_carrier' ? 'bg-sky-50 text-sky-700 border-sky-200' :
    s === 'received'             ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                    'bg-slate-50 text-slate-700 border-slate-200';

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><FileText size={11}/> Document Collection ({(docs || []).length} outstanding)</div>
        <button onClick={() => setShow(!show)} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={11}/> Request document</button>
      </div>
      {show && (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={f.client_name} onChange={e => setF({ ...f, client_name: e.target.value })} placeholder="Client name" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <select value={f.document_kind} onChange={e => setF({ ...f, document_kind: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              {DOC_KINDS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </div>
          <input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder='e.g. "Last 2 pay stubs"' className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="date" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <select value={f.requested_via} onChange={e => setF({ ...f, requested_via: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="call">Call</option>
              <option value="manual">Manual</option>
            </select>
          </div>
          <button onClick={request} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-1.5 rounded-md">Request</button>
        </div>
      )}
      {(docs || []).length === 0 ? (
        <div className="px-4 py-4 text-sm text-slate-500 italic">No outstanding document requests.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {(docs || []).slice(0, 30).map(d => (
            <DocRow key={d.id} d={d} setStatus={setStatus} onReload={onReload} showToast={showToast} statusTone={statusTone} openClientProfile={openClientProfile}/>
          ))}
        </div>
      )}
    </div>
  );
}

// Single document-request row with inline file-upload (Vercel Blob).
// Drag a file OR click → upload streams DIRECT to Blob via a one-time
// signed token from /api/uploads/sign. When the upload completes the
// server-side onUploadCompleted hook flips status='received' +
// stamps artifact_url, so this row repaints with the artifact link
// on the next refresh.
function DocRow({ d, setStatus, onReload, showToast, statusTone, openClientProfile = () => {} }) {
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);

  const handleUpload = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const jwt = readPersistedJwtDirect();
      if (!jwt) throw new Error('Not signed in');
      const blob = await upload(`docs/${d.id}/${file.name}`, file, {
        access:           'public',
        handleUploadUrl:  '/api/uploads/sign',
        clientPayload:    JSON.stringify({ document_request_id: d.id, target_status: 'received' }),
        // upload() doesn't accept custom auth headers — the server
        // reads the bearer via the fetch wrapper below.
        headers:          { Authorization: `Bearer ${jwt}` },
      });
      showToast(`Uploaded — ${file.name}`);
      // Server hook already flipped status + stored artifact_url; refresh.
      onReload();
    } catch (err) {
      showToast(`Upload failed: ${err.message || err}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  // When status='requested', show the drag-drop zone inline. When
  // upload completes (status flips received/submitted), show the
  // artifact link instead.
  return (
    <div
      onDrop={d.status === 'requested' ? onDrop : undefined}
      onDragOver={d.status === 'requested' ? onDragOver : undefined}
      onDragLeave={d.status === 'requested' ? onDragLeave : undefined}
      className={`px-4 py-2 flex items-center gap-3 ${dragOver ? 'bg-emerald-50' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-900 flex items-center gap-2 flex-wrap">
          {d.client_name
            ? <button type="button" onClick={() => openClientProfile(d.client_id || d.client_name)} className="hover:underline">{d.client_name}</button>
            : '—'}
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{String(d.document_kind).replace(/_/g, ' ')}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${statusTone(d.status)}`}>{String(d.status).replace(/_/g, ' ')}</span>
        </div>
        {d.description && <div className="text-xs text-slate-600 mt-0.5">{d.description}</div>}
        {d.due_date && <div className="text-[11px] text-slate-500 mt-0.5">Due {d.due_date}</div>}
        {d.artifact_url && (
          <a href={d.artifact_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-sky-700 hover:text-sky-900 underline mt-0.5 inline-block">View uploaded file →</a>
        )}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {d.status === 'requested' && (
          <>
            <input ref={fileInputRef} type="file" hidden onChange={e => handleUpload(e.target.files?.[0])}/>
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="text-[10px] font-bold uppercase text-sky-700 hover:text-sky-900 px-2 py-1 border border-sky-200 rounded inline-flex items-center gap-1">
              <Upload size={10}/> {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <button onClick={() => setStatus(d.id, 'received')} className="text-[10px] font-bold uppercase text-emerald-700 hover:text-emerald-900 px-2 py-1 border border-emerald-200 rounded">Mark received</button>
          </>
        )}
        {d.status === 'received'             && <button onClick={() => setStatus(d.id, 'submitted_to_carrier')} className="text-[10px] font-bold uppercase text-sky-700 hover:text-sky-900 px-2 py-1 border border-sky-200 rounded">Submit→carrier</button>}
        {d.status === 'submitted_to_carrier' && <button onClick={() => setStatus(d.id, 'accepted')} className="text-[10px] font-bold uppercase text-emerald-700 hover:text-emerald-900 px-2 py-1 border border-emerald-200 rounded">Accepted</button>}
        {d.status === 'submitted_to_carrier' && <button onClick={() => { const r = prompt('Rejection reason?'); if (r) setStatus(d.id, 'rejected', { rejected_reason: r }); }} className="text-[10px] font-bold uppercase text-rose-700 hover:text-rose-900 px-2 py-1 border border-rose-200 rounded">Rejected</button>}
      </div>
    </div>
  );
}

// ── #4 FFM / Marketplace Account ────────────────────────────────────
function FFMAccountPanel({ credentials, onReload, showToast }) {
  const [f, setF] = React.useState({
    npi:                  credentials?.npi || '',
    ffm_user_id:          credentials?.ffm_user_id || '',
    healthsherpa_user_id: credentials?.healthsherpa_user_id || '',
    aor_status:           credentials?.aor_status || 'none',
    aor_renewed_on:       credentials?.aor_renewed_on || '',
    states_licensed:      (credentials?.states_licensed || []).join(', '),
    carriers_appointed:   (credentials?.carriers_appointed || []).join(', '),
    income_change_alerts_enabled: credentials?.income_change_alerts_enabled !== false,
  });
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (credentials) setF({
      npi:                  credentials.npi || '',
      ffm_user_id:          credentials.ffm_user_id || '',
      healthsherpa_user_id: credentials.healthsherpa_user_id || '',
      aor_status:           credentials.aor_status || 'none',
      aor_renewed_on:       credentials.aor_renewed_on || '',
      states_licensed:      (credentials.states_licensed || []).join(', '),
      carriers_appointed:   (credentials.carriers_appointed || []).join(', '),
      income_change_alerts_enabled: credentials.income_change_alerts_enabled !== false,
    });
  }, [credentials]);

  const save = async () => {
    setBusy(true);
    try {
      await apiFetch('/api/aca/actions', {
        method: 'POST',
        body: JSON.stringify({
          action: 'ffm_save',
          ...f,
          states_licensed:    f.states_licensed.split(',').map(s => s.trim()).filter(Boolean),
          carriers_appointed: f.carriers_appointed.split(',').map(s => s.trim()).filter(Boolean),
          aor_renewed_on:     f.aor_renewed_on || null,
        }),
      });
      showToast('FFM credentials saved.');
      onReload();
    } catch (err) { showToast(`Save failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><ShieldCheck size={11}/> FFM / Marketplace Account</div>
      <div className="px-4 py-3 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-xs text-slate-600">NPI
            <input value={f.npi} onChange={e => setF({ ...f, npi: e.target.value })} placeholder="National Producer #" className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">FFM Agent Broker Portal ID
            <input value={f.ffm_user_id} onChange={e => setF({ ...f, ffm_user_id: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">HealthSherpa user ID
            <input value={f.healthsherpa_user_id} onChange={e => setF({ ...f, healthsherpa_user_id: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">AOR status
            <select value={f.aor_status} onChange={e => setF({ ...f, aor_status: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              <option value="none">None</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </label>
          <label className="text-xs text-slate-600">AOR renewed on
            <input type="date" value={f.aor_renewed_on} onChange={e => setF({ ...f, aor_renewed_on: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
        </div>
        <label className="text-xs text-slate-600">States licensed (comma-separated, e.g. TX, FL, GA)
          <input value={f.states_licensed} onChange={e => setF({ ...f, states_licensed: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
        </label>
        <label className="text-xs text-slate-600">Carriers appointed (comma-separated)
          <input value={f.carriers_appointed} onChange={e => setF({ ...f, carriers_appointed: e.target.value })} className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
        </label>
        <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
          <input type="checkbox" checked={f.income_change_alerts_enabled} onChange={e => setF({ ...f, income_change_alerts_enabled: e.target.checked })}/>
          Alert me when a client's income changes (so I can update their FFM record before APTC reconciliation hits)
        </label>
        <button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-1.5 rounded-md">{busy ? 'Saving…' : 'Save credentials'}</button>
      </div>
    </div>
  );
}
